import * as poseDetection from '@tensorflow-models/pose-detection';
import '@tensorflow/tfjs-backend-webgl';
import * as tf from '@tensorflow/tfjs-core';
import { MOVENET_SKELETON } from './MoveNetSkeleton';
import {
  sourceHeight,
  sourceWidth,
  type PoseEngine,
  type PoseSource,
  type RawPose,
  type RawPoseResult,
  type SkeletonDefinition,
} from './PoseEngine';

export interface MoveNetOptions {
  modelType?: 'lightning' | 'thunder';
  minScore?: number;
}

export class MoveNetPoseEngine implements PoseEngine {
  readonly id = 'movenet-singlepose';
  readonly label: string;
  readonly skeleton: SkeletonDefinition = MOVENET_SKELETON;
  readonly supportsMultiPose = false;
  private detector: poseDetection.PoseDetector | null = null;

  constructor(private readonly opts: MoveNetOptions = {}) {
    const t = opts.modelType ?? 'lightning';
    this.label = t === 'thunder' ? 'MoveNet Thunder (17pt)' : 'MoveNet Lightning (17pt)';
  }

  async initialize(): Promise<void> {
    await tf.setBackend('webgl');
    await tf.ready();
    const modelType =
      this.opts.modelType === 'thunder'
        ? poseDetection.movenet.modelType.SINGLEPOSE_THUNDER
        : poseDetection.movenet.modelType.SINGLEPOSE_LIGHTNING;
    this.detector = await poseDetection.createDetector(poseDetection.SupportedModels.MoveNet, {
      modelType,
    });
  }

  async setNumPoses(_n: number): Promise<void> {
    // SinglePose only; ignore
  }

  async detectForVideo(source: PoseSource, _timestamp: number): Promise<RawPoseResult> {
    if (!this.detector) {
      throw new Error('MoveNetPoseEngine not initialized');
    }
    const w = sourceWidth(source) || 1;
    const h = sourceHeight(source) || 1;
    const detected = await this.detector.estimatePoses(source, {
      maxPoses: 1,
      flipHorizontal: false,
    });
    const poses: RawPose[] = detected.map((p) => ({
      landmarks: p.keypoints.map((kp) => ({
        x: kp.x / w,
        y: kp.y / h,
        score: kp.score ?? 0,
      })),
    }));
    return { poses };
  }

  dispose(): void {
    this.detector?.dispose();
    this.detector = null;
  }
}
